'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { getDb } from '@/lib/db';
import { formatDateKey, formatMonthKey } from '@/lib/date';
import { STORAGE_UNAVAILABLE_MESSAGE, storageErrorMessage } from '@/lib/storageErrors';
import { Category, MonthlyStats, RecurringExpense, SavingsGoal, Transaction } from '@/lib/types';

function startOfMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function startOfNextMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 1);
}

export function useMonthlyStats() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [recurringExpenses, setRecurringExpenses] = useState<RecurringExpense[]>([]);
  const [savingsGoal, setSavingsGoal] = useState<SavingsGoal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      setLoading(false);
      return;
    }
    setLoading(true);
    const now = new Date();
    const monthStart = formatDateKey(startOfMonth(now));
    const nextMonthStart = formatDateKey(startOfNextMonth(now));
    const monthKey = formatMonthKey(now);
    try {
      const allTxs = await db.transactions.toArray();
      const txs = allTxs.filter((tx) => tx.date >= monthStart && tx.date < nextMonthStart);
      const cats = await db.categories.toArray();
      const recurring = (await db.recurringExpenses.toArray()).filter((item) => item.active);
      const goal = (await db.savingsGoals.where('month').equals(monthKey).first()) ?? null;
      setTransactions(txs);
      setCategories(cats);
      setRecurringExpenses(recurring);
      setSavingsGoal(goal);
      setError(null);
    } catch (err) {
      setError(storageErrorMessage('Impossible de charger les statistiques du mois.', err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener('budget-db-update', handler);
    return () => window.removeEventListener('budget-db-update', handler);
  }, [load]);

  const stats = useMemo<MonthlyStats>(() => {
    const income = transactions.filter((tx) => tx.type === 'income').reduce((sum, tx) => sum + tx.amount, 0);
    const expense = transactions.filter((tx) => tx.type === 'expense').reduce((sum, tx) => sum + tx.amount, 0);
    const categorySpending = categories.reduce<Record<string, number>>((acc, category) => {
      acc[category.id] = 0;
      return acc;
    }, {});

    transactions.forEach((tx) => {
      if (tx.type === 'expense' && tx.categoryId) {
        categorySpending[tx.categoryId] = (categorySpending[tx.categoryId] ?? 0) + tx.amount;
      }
    });

    const topCategories = Object.entries(categorySpending)
      .map(([id, amount]) => ({ id, amount }))
      .filter((item) => item.amount > 0)
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 5)
      .map((item) => {
        const category = categories.find((cat) => cat.id === item.id);
        return {
          name: category?.name ?? 'Sans catégorie',
          amount: item.amount,
        };
      });

    const totalBudget = categories.reduce((sum, cat) => sum + (cat.monthlyBudget || 0), 0);
    const recurringExpense = recurringExpenses.reduce((sum, item) => sum + item.amount, 0);
    const savingsGoalAmount = savingsGoal?.amount ?? 0;
    const now = new Date();
    const dailyCount = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const dailyAverage = expense / Math.max(dailyCount, 1);
    const monthActualBalance = income - expense;

    return {
      monthBalance: monthActualBalance - recurringExpense - savingsGoalAmount,
      monthActualBalance,
      monthIncome: income,
      monthExpense: expense,
      recurringExpense,
      savingsGoal: savingsGoalAmount,
      globalBudgetRemaining: totalBudget - expense,
      categorySpending,
      topCategories,
      dailyAverage,
      totalDays: dailyCount,
    };
  }, [transactions, categories, recurringExpenses, savingsGoal]);

  return {
    stats,
    loading,
    error,
    reload: load,
  };
}
